/**
 * web/src/components/shared/StatsBanner.tsx
 * 4-column stats banner (handoff §Components §Stats / §Workflows §Stats
 * banner). 1px outer border, 8px radius, vertical 1px dividers, no
 * internal gutters. Each stat: 11/uppercase label, 22/600/tabular value,
 * optional 11/subtle delta. Clickable variant routes to the filtered list
 * (UI-03 — workflow list filter by status).
 *
 * Responsive: 2-col grid below sm (CONTEXT.md D-3.1/D-3.2), 4-col at sm+.
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

/** A single stat slot. `onClick` makes the cell behave as a filter trigger. */
export interface StatItem {
  label: string;
  value: ReactNode;
  delta?: ReactNode;
  onClick?: () => void;
  valueClassName?: string;
}

/**
 * Renders a horizontal stats banner of 4 stats (typically). Stacks to a
 * 2×2 grid below the `sm` breakpoint per CONTEXT.md D-3.2 mobile pattern.
 *
 * Side effects: none — invokes `s.onClick` when supplied (a callback the
 * caller wires to a router push).
 */
export function StatsBanner({ stats, className }: { stats: StatItem[]; className?: string }) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 sm:grid-cols-4 border border-border rounded-lg overflow-hidden bg-bg',
        className,
      )}
    >
      {stats.map((s, i) => (
        <button
          key={i}
          type="button"
          onClick={s.onClick}
          className={cn(
            'flex flex-col items-start gap-1 px-5 py-4 text-left',
            i > 0 && 'sm:border-l border-border',
            s.onClick && 'hover:bg-bg-hover transition-colors cursor-pointer',
            !s.onClick && 'cursor-default',
          )}
        >
          <span className="text-[11px] font-medium uppercase tracking-wider text-text-subtle">
            {s.label}
          </span>
          <span
            className={cn('text-[22px] font-semibold tabular -tracking-[0.02em]', s.valueClassName)}
          >
            {s.value}
          </span>
          {s.delta !== undefined && <span className="text-[11px] text-text-subtle">{s.delta}</span>}
        </button>
      ))}
    </div>
  );
}
